import type {
  BuyerDealStructureFiguresDto,
  DealStructureCashFlowType,
  DealStructurePriceBasis,
} from "@/api/buyers";
import { Input, ResponsiveGrid, Textarea } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import {
  CASH_FLOW_TYPES,
  CASH_FLOW_TYPE_LABELS,
  NOTE_MAX_LENGTH,
  W1,
  W2,
  W3,
  W4,
  parseMoneyText,
} from "./dealStructureCopy";

/**
 * The deal structure's input fields, shared by BUILD THE DEAL STRUCTURE (one
 * step each) and the EDIT modal (all at once) — "same fields as the wizard"
 * holds by construction rather than by two copies kept in step.
 *
 * <p>Money inputs are `type="text"` + `inputMode="decimal"`: a number input
 * reports `""` for both cleared and malformed.</p>
 */

const fieldLabel = "block text-caption text-gray-mid";

/** W1 — the cash-flow type, the figure, and (SDE only) the free cash flow estimate. */
export function CashFlowFields({
  idPrefix,
  type,
  onType,
  amountText,
  onAmountText,
  estimateText,
  onEstimateText,
}: {
  idPrefix: string;
  type: DealStructureCashFlowType | null;
  onType: (t: DealStructureCashFlowType) => void;
  amountText: string;
  onAmountText: (v: string) => void;
  estimateText: string;
  onEstimateText: (v: string) => void;
}) {
  const amountInvalid = amountText.trim() !== "" && parseMoneyText(amountText) == null;
  const estimateInvalid = estimateText.trim() !== "" && parseMoneyText(estimateText) == null;
  return (
    <div className="grid gap-space-3">
      <div role="radiogroup" aria-label="Cash-flow type" className="flex flex-wrap gap-space-2">
        {CASH_FLOW_TYPES.map((t) => (
          <ChoiceChip
            key={t}
            selected={type === t}
            onSelect={() => onType(t)}
            testId={`${idPrefix}-type-${t}`}
          >
            {CASH_FLOW_TYPE_LABELS[t]}
          </ChoiceChip>
        ))}
      </div>
      <div className="grid gap-space-1">
        <label className={fieldLabel} htmlFor={`${idPrefix}-cash-flow`}>{W1.amountLabel}</label>
        <Input
          id={`${idPrefix}-cash-flow`}
          data-testid={`${idPrefix}-cash-flow`}
          inputMode="decimal"
          value={amountText}
          onChange={(e) => onAmountText(e.target.value)}
        />
        {amountInvalid && <p className="text-caption text-status-error m-0">{W1.amountAboveZero}</p>}
      </div>
      {type === "Sde" && (
        <div className="grid gap-space-1" data-testid={`${idPrefix}-sde`}>
          <label className={fieldLabel} htmlFor={`${idPrefix}-fcf-estimate`}>{W1.sdeEstimateLabel}</label>
          <Input
            id={`${idPrefix}-fcf-estimate`}
            data-testid={`${idPrefix}-fcf-estimate`}
            inputMode="decimal"
            required
            value={estimateText}
            onChange={(e) => onEstimateText(e.target.value)}
          />
          {estimateInvalid && (
            <p className="text-caption text-status-error m-0">{W1.amountAboveZero}</p>
          )}
          <p className="text-caption text-gray-mid m-0" data-testid={`${idPrefix}-sde-note`}>{W1.sdeNote}</p>
        </div>
      )}
    </div>
  );
}

/** True when the W1 fields hold a value the server can record. */
export function cashFlowReady(
  type: DealStructureCashFlowType | null,
  amountText: string,
  estimateText: string,
): boolean {
  if (!type || parseMoneyText(amountText) == null) return false;
  return type !== "Sde" || parseMoneyText(estimateText) != null;
}

/** W2 — the down payment. $0 is allowed, with Appendix B's warning. */
export function DownPaymentField({
  idPrefix,
  text,
  onText,
}: {
  idPrefix: string;
  text: string;
  onText: (v: string) => void;
}) {
  const value = parseMoneyText(text, true);
  const invalid = text.trim() !== "" && value == null;
  return (
    <div className="grid gap-space-1">
      <label className={fieldLabel} htmlFor={`${idPrefix}-down-payment`}>{W2.amountLabel}</label>
      <Input
        id={`${idPrefix}-down-payment`}
        data-testid={`${idPrefix}-down-payment`}
        inputMode="decimal"
        value={text}
        onChange={(e) => onText(e.target.value)}
      />
      {invalid && (
        <p className="text-caption text-status-error m-0">Enter the down payment in dollars.</p>
      )}
      {value === 0 && (
        <p role="alert" className="text-caption text-status-warning-ink m-0" data-testid={`${idPrefix}-zero-down`}>
          {W2.zeroWarning}
        </p>
      )}
    </div>
  );
}

/**
 * W3 — the three price cards, side by side. The asking and supported figures
 * are the server's (`figures`); the client never computes a price.
 *
 * <p>`supportedStale` is for the EDIT modal: when the cash flow or the down
 * payment has been changed in the form but not saved, the saved supported
 * price no longer describes it, so the card says it is calculated on save
 * rather than quoting one figure and storing another.</p>
 */
export function PriceBasisCards({
  idPrefix,
  basis,
  onBasis,
  customText,
  onCustomText,
  figures,
  supportedStale = false,
}: {
  idPrefix: string;
  basis: DealStructurePriceBasis | null;
  onBasis: (b: DealStructurePriceBasis) => void;
  customText: string;
  onCustomText: (v: string) => void;
  figures: BuyerDealStructureFiguresDto;
  supportedStale?: boolean;
}) {
  // D7 — the price is uncapped (the $10 million limit is on the amount
  // financed), so only a missing asking price disables the card.
  const askingDisabledReason = figures.askingPrice == null ? W3.noAsking : null;
  const supported = supportedStale ? null : figures.supportedPrice ?? null;
  return (
    <div role="radiogroup" aria-label="Price basis">
      <ResponsiveGrid cols={3} minColWidth="12rem" gap="sm">
        <PriceCard
          testId={`${idPrefix}-basis-Asking`}
          title={W3.asking}
          selected={basis === "Asking"}
          disabled={askingDisabledReason != null}
          onSelect={() => onBasis("Asking")}
        >
          {figures.askingPrice != null && (
            <span className="text-body text-onyx" data-testid={`${idPrefix}-asking-figure`}>
              {formatCurrency(figures.askingPrice)}
            </span>
          )}
          {askingDisabledReason && (
            <span className="text-caption text-status-warning-ink" data-testid={`${idPrefix}-asking-disabled`}>
              {askingDisabledReason}
            </span>
          )}
        </PriceCard>
        <PriceCard
          testId={`${idPrefix}-basis-Supported`}
          title={W3.supported}
          selected={basis === "Supported"}
          disabled={supported == null && !supportedStale}
          onSelect={() => onBasis("Supported")}
        >
          <span className="text-small text-onyx" data-testid={`${idPrefix}-supported-figure`}>
            {supported != null
              ? `${formatCurrency(supported)}${W3.supportedTail}`
              : supportedStale
                ? `Calculated when you save${W3.supportedTail}`
                : `Available once the cash flow and down payment are saved${W3.supportedTail}`}
          </span>
        </PriceCard>
        <PriceCard
          testId={`${idPrefix}-basis-Custom`}
          title={W3.custom}
          selected={basis === "Custom"}
          onSelect={() => onBasis("Custom")}
        >
          <label className="sr-only" htmlFor={`${idPrefix}-custom-price`}>{W3.customLabel}</label>
          <Input
            id={`${idPrefix}-custom-price`}
            data-testid={`${idPrefix}-custom-price`}
            inputMode="decimal"
            placeholder={W3.customPlaceholder}
            value={customText}
            onFocus={() => onBasis("Custom")}
            onChange={(e) => {
              onBasis("Custom");
              onCustomText(e.target.value);
            }}
          />
        </PriceCard>
      </ResponsiveGrid>
    </div>
  );
}

/** True when the W3 choice holds a value the server can record. */
export function priceReady(basis: DealStructurePriceBasis | null, customText: string): boolean {
  if (!basis) return false;
  return basis !== "Custom" || parseMoneyText(customText) != null;
}

/** W4 — the optional Phoenix note, with its counter. */
export function NoteField({
  idPrefix,
  text,
  onText,
}: {
  idPrefix: string;
  text: string;
  onText: (v: string) => void;
}) {
  return (
    <div className="grid gap-space-1">
      <label className={fieldLabel} htmlFor={`${idPrefix}-note`}>{W4.noteLabel}</label>
      <Textarea
        id={`${idPrefix}-note`}
        data-testid={`${idPrefix}-note`}
        rows={3}
        maxLength={NOTE_MAX_LENGTH}
        value={text}
        onChange={(e) => onText(e.target.value)}
      />
      <span className="text-caption text-gray-mid justify-self-end" data-testid={`${idPrefix}-note-counter`}>
        {`${text.length}/${NOTE_MAX_LENGTH}`}
      </span>
    </div>
  );
}

function ChoiceChip({
  selected,
  onSelect,
  testId,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  testId: string;
  children: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={testId}
      onClick={onSelect}
      className={cn(
        "rounded-pill border px-space-3 py-space-1 text-small transition-colors duration-fast ease-standard",
        selected
          ? "border-gold bg-gold text-onyx font-semibold"
          : "border-border-light bg-white-pure text-onyx hover:border-gold",
      )}
    >
      {children}
    </button>
  );
}

function PriceCard({
  title,
  selected,
  disabled = false,
  onSelect,
  testId,
  children,
}: {
  title: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      data-testid={testId}
      onClick={() => !disabled && onSelect()}
      onKeyDown={(e) => {
        if (disabled || e.target !== e.currentTarget) return;
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "grid gap-space-1 content-start rounded-md border p-space-3 min-w-0",
        disabled
          ? "cursor-not-allowed border-border-light bg-white-warm"
          : "cursor-pointer bg-white-pure hover:border-gold",
        selected ? "border-gold ring-1 ring-gold" : "border-border-light",
      )}
    >
      <span className="text-small font-semibold text-onyx">{title}</span>
      {children}
    </div>
  );
}
